// Wire-format types and API contract for PRO Item Design (PRD 04).
// Mirrors the shape used by `diseaseCodeApi` so adapter/error patterns stay
// consistent across features.
//
// Status follows PRD §3.4 recommendation (DRAFT / PUBLISHED, English).
// Condition operators follow the full PRD §3.3 set (7 ops).
// First-execution is stored as a structured discriminated union (FE decision
// confirmed during planning) rather than the prototype's stringified label.

// ── Status, scope, schedule enums ───────────────────────────────────────────

export type ProItemStatus = 'DRAFT' | 'PUBLISHED';

export const PRO_ITEM_STATUSES: readonly ProItemStatus[] = ['DRAFT', 'PUBLISHED'] as const;

// PRD §3.2.
export type ProItemScope = 'COMMON' | 'DISEASE_GROUP' | 'DISEASE_SPECIFIC';

export const PRO_ITEM_SCOPES: readonly ProItemScope[] = [
  'COMMON',
  'DISEASE_GROUP',
  'DISEASE_SPECIFIC',
] as const;

// PRD §3.2 — measurement cycle.
export type ProMeasurementCycle =
  | 'DAILY'
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY';

export const PRO_MEASUREMENT_CYCLES: readonly ProMeasurementCycle[] = [
  'DAILY',
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'QUARTERLY',
] as const;

// Day-of-week stored as ISO short codes so BE and FE share the same vocabulary.
export type DayOfWeek = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export const DAYS_OF_WEEK: readonly DayOfWeek[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
] as const;

// PRD §3.2 — first measurement trigger. `N_DAYS_AFTER` requires `nDays` in
// the parent payload; the FE validates 1..365.
export type FirstExecutionKind =
  | 'DAY_AFTER_DISCHARGE'
  | 'WEEK_AFTER_DISCHARGE'
  | 'N_DAYS_AFTER';

export interface FirstExecution {
  kind: FirstExecutionKind;
  // Only set (and required) when kind === 'N_DAYS_AFTER'. Range 1..365.
  nDays?: number;
}

// SHCT-776 — the cycle → 첫 시행 기준 derivation lives in `utils/item-validation.ts`, beside
// requiresDaysOfWeek, its sibling cycle rule. It is deliberately NOT here: this file is
// excluded from coverage as "type-only declarations (no runtime code)" (vite.config.ts), so a
// runtime rule placed here would be untestable by the project's own gate.

// PRD §3.3 — grade calculation method.
export type GradeMethod = 'SUM' | 'COMPLEX';

export type GradeKey = 'g1' | 'g2' | 'g3';

export const GRADE_KEYS: readonly GradeKey[] = ['g1', 'g2', 'g3'] as const;

// PRD §3.3 — full operator set. Includes `∈` (membership) for multi-value
// answers; `≠`/`≥`/`≤`/`>`/`<` available for ordinal answers.
export type ConditionOperator =
  | 'EQ'  // =
  | 'NEQ' // ≠
  | 'GTE' // ≥
  | 'LTE' // ≤
  | 'GT'  // >
  | 'LT'  // <
  | 'IN'; // ∈

export const CONDITION_OPERATORS: readonly ConditionOperator[] = [
  'EQ',
  'NEQ',
  'GTE',
  'LTE',
  'GT',
  'LT',
  'IN',
] as const;

export type LogicOperator = 'AND' | 'OR';

// ── Tree structure (Category > SubArea > Item) ──────────────────────────────

export interface ProDomain {
  id: number;
  // Display name. PRD §4.2 — required.
  name: string;
  description: string | null;
  // 0-based position within the tree's root list. Auto-numbered N (1-based) for display.
  order: number;
  subAreas: ProSubArea[];
}

export interface ProSubArea {
  id: number;
  domainId: number;
  name: string;
  description: string | null;
  order: number;
  // Items are returned as headers only on the tree endpoint (id + pid + name +
  // status + schedule summary). The full ProItem comes from getItem().
  items: ProItemHeader[];
}

// Lightweight item entry returned in the tree (without the heavy detail).
export interface ProItemHeader {
  id: number;
  domainId: number;
  subAreaId: number;
  // 'P-####' — system-assigned on creation, zero-padded to 4 digits. PRD §4.1.
  pid: string;
  name: string;
  status: ProItemStatus;
  order: number;
  // Inline schedule summary so the tree row can render '주1회(월)' without a
  // per-item detail fetch. May be null until the item has a saved schedule.
  scheduleSummary: string | null;
  // FEATURE_028 (SHCT-590) — server-side "changed but not yet deployed" marker
  // that drives the yellow 수정 badge. Optional because a locally-created draft
  // node (draft-node.ts) has no server row yet; treat a missing value as false.
  dirty?: boolean;
}

// PRD §3.2 — full item detail returned by getItem().
export interface ProItem extends ProItemHeader {
  description: string | null;
  scope: ProItemScope;
  // Required when scope === 'DISEASE_GROUP'. Null otherwise.
  diseaseGroupId: number | null;
  // Required when scope === 'DISEASE_SPECIFIC'. Null otherwise.
  diseaseCodeId: number | null;
  // Null until the item has a saved schedule — a freshly-created DRAFT has none
  // yet (mirrors `scheduleSummary` above). Consumers must default it.
  schedule: ProSchedule | null;
  linkedQuestions: LinkedQuestion[];
  gradeMethod: GradeMethod;
  // Set only when gradeMethod === 'SUM'. PRD §3.3.
  gradeSum: GradeSumConfig | null;
  // Set only when gradeMethod === 'COMPLEX'. PRD §3.3.
  gradeComplex: GradeComplexConfig | null;
  // GID linked per grade. Required on publish (G1/G2/G3 all set). PRD §3.3.
  gradeGuidelines: GradeGuidelineMap;
  // ISO-8601 UTC. Maintained by BE.
  updatedAt: string;
}

export interface ProSchedule {
  cycle: ProMeasurementCycle;
  // Empty allowed only while DRAFT. PUBLISH requires at least one day; if cycle
  // is 'DAILY', BE auto-fills all 7 days.
  daysOfWeek: DayOfWeek[];
  firstExecution: FirstExecution;
}

// PRD §3.2 — linked from Questionnaire Design (auto-sync). FE does not write
// this list; FE only edits `weight` and `confidence` per row (TBD per PRD).
export interface LinkedQuestion {
  qid: string;
  title: string;
  target: 'PATIENT' | 'GUARDIAN';
  // Display string from Questionnaire (e.g. '0~3', '0~10'). FE renders as-is.
  scoreRange: string;
  // Optional answer enum — when present, drives the value picker for complex
  // condition rows (PRD §3.3).
  answers: string[] | null;
  // Per-item weight (0.01..1.00). FE-editable. TBD per PRD §3.2 (research review).
  weight: number;
  // Per-item confidence/reliability (0.01..1.00). FE-editable. TBD per PRD §3.2.
  confidence: number;
}

// PRD §3.3 — sum mode. G1: 0..g1Max, G2: g1Max+1..g2Max, G3: g2Max+1..∞.
// Validation: g2Max > g1Max strictly (no overlap, no gap).
export interface GradeSumConfig {
  g1Max: number;
  g2Max: number;
}

// PRD §3.3 — complex mode. Each grade is a list of condition rows; the first
// row has no logic, subsequent rows carry AND/OR.
export interface ConditionRow {
  // Linked question's `qid` (must match an entry in `linkedQuestions`).
  qid: string;
  op: ConditionOperator;
  // Single value for scalar ops; comma-separated stringified list for `IN`.
  value: string;
  // Logic operator connecting this row to the previous row in the same grade.
  // `null` for the first row. PRD §3.3.
  logic: LogicOperator | null;
}

export type GradeComplexConfig = Record<GradeKey, ConditionRow[]>;

// PRD §3.3 — GID per grade. `null` allowed only while DRAFT.
export type GradeGuidelineMap = Record<GradeKey, string | null>;

// ── Inputs ──────────────────────────────────────────────────────────────────

export interface CreateDomainInput {
  name: string;
  description?: string | null;
}

export type UpdateDomainInput = Partial<CreateDomainInput>;

export interface CreateSubAreaInput {
  name: string;
  description?: string | null;
}

export type UpdateSubAreaInput = Partial<CreateSubAreaInput>;

export interface CreateItemInput {
  // Initial name. All other fields default to empty/null and are filled via
  // saveDraftItem / publishItem.
  name: string;
}

// Used by both saveDraftItem and publishItem. The two endpoints differ only
// in the validation strictness BE applies (DRAFT skips required-field checks).
export interface SaveItemInput {
  name: string;
  description?: string | null;
  scope: ProItemScope;
  diseaseGroupId?: number | null;
  diseaseCodeId?: number | null;
  schedule: ProSchedule;
  gradeMethod: GradeMethod;
  gradeSum?: GradeSumConfig | null;
  gradeComplex?: GradeComplexConfig | null;
  gradeGuidelines: GradeGuidelineMap;
  // Per-question weight/confidence overrides. Keys must match
  // `linkedQuestions[].qid`. Omitted qids keep their current values.
  weights?: Record<string, number>;
  confidences?: Record<string, number>;
}

// PRD §3.1 — reorder a sibling list (domains under root, sub-areas under a
// domain, or items under a sub-area).
export type ReorderKind = 'DOMAIN' | 'SUB_AREA' | 'ITEM';

export interface ReorderInput {
  kind: ReorderKind;
  // Required for SUB_AREA (parent domainId) and ITEM (parent subAreaId).
  // Omitted/null for DOMAIN reorder.
  parentId: number | null;
  // Full ordered list of sibling ids after the move. BE re-numbers from this list.
  orderedIds: number[];
}

// ── Error envelope ──────────────────────────────────────────────────────────

export type ProItemDesignApiErrorCode =
  // Domain / sub-area
  | 'NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'MAX_DEPTH_EXCEEDED'
  // Item — required fields
  | 'SCOPE_REQUIRED'
  | 'DISEASE_GROUP_REQUIRED'
  | 'DISEASE_CODE_REQUIRED'
  | 'CYCLE_REQUIRED'
  | 'DAYS_REQUIRED'
  | 'FIRST_EXECUTION_REQUIRED'
  | 'FIRST_EXECUTION_CYCLE_MISMATCH'
  | 'N_DAYS_OUT_OF_RANGE'
  // Item — weights / grade
  | 'WEIGHT_OUT_OF_RANGE'
  | 'CONFIDENCE_OUT_OF_RANGE'
  | 'GRADE_METHOD_REQUIRED'
  | 'GRADE_RANGE_OVERLAP'
  | 'GRADE_RANGE_MISSING'
  | 'GRADE_GUIDELINE_REQUIRED'
  | 'GRADE_CONDITION_REQUIRED'
  // Linked questions
  | 'DUPLICATE_QID_LINK'
  // Platform / canonical
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  | 'UNAUTHORIZED'
  | 'UNKNOWN';

export class ProItemDesignApiError extends Error {
  readonly code: ProItemDesignApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: ProItemDesignApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'ProItemDesignApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, ProItemDesignApiError.prototype);
  }
}

// ── API surface ─────────────────────────────────────────────────────────────

export interface ProItemDesignApi {
  // PRD §3.1 — full tree (Category > SubArea > ItemHeader). Items in the tree
  // are headers only; call getItem(id) for the full detail.
  getTree(): Promise<ProDomain[]>;

  // PRD §3.2 — full item detail for the right-panel form.
  getItem(itemId: number): Promise<ProItem>;

  // Domain CRUD. Order is appended; reorderTree() moves explicitly.
  createDomain(input: CreateDomainInput): Promise<ProDomain>;
  updateDomain(id: number, input: UpdateDomainInput): Promise<ProDomain>;
  // Cascade delete (matches prototype): all child sub-areas and items removed by BE.
  deleteDomain(id: number): Promise<void>;

  // SubArea CRUD.
  createSubArea(domainId: number, input: CreateSubAreaInput): Promise<ProSubArea>;
  updateSubArea(id: number, input: UpdateSubAreaInput): Promise<ProSubArea>;
  // Cascade delete (matches prototype): all child items removed by BE.
  deleteSubArea(id: number): Promise<void>;

  // Item CRUD. Create returns a DRAFT item's header only (no linkedQuestions /
  // scope / schedule) — callers re-fetch the full item via getItem for the form.
  createItem(subAreaId: number, input: CreateItemInput): Promise<ProItemHeader>;
  // PRD §3.4 — Save Draft. BE skips required-field checks; FE form validates
  // ranges and per-field constraints client-side.
  saveDraftItem(id: number, input: SaveItemInput): Promise<ProItem>;
  // PRD §3.4 — Save (publish). BE enforces the full required-field set; raises
  // a typed error if a required value is missing.
  publishItem(id: number, input: SaveItemInput): Promise<ProItem>;
  deleteItem(id: number): Promise<void>;

  // PRD §3.1 — sibling reorder (up/down arrows in the tree).
  reorder(input: ReorderInput): Promise<void>;
}
